import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { graphql, navigate, StaticQuery } from 'gatsby';
import { Header as DlsHeader } from '@singtel-web/widgets';
import { SingtelWebSearchBar, useSearchToggle } from '@common-modules/search';
import { getCookie } from '@lux/helpers';
import { HeaderWrapper } from './style';
import { getUIAMLoginUrl, sdAccountSummary } from '../../config';
import { api, apigeeAuthActions, eshopUserActions } from '@detox/actions';
import { useDispatch } from 'react-redux';
import useInterceptor from '../../hooks/useInterceptor';
import CONSTANT from '../../constants/common';
import useIsWebViewFlow from '../../hooks/useIsWebViewFlow';
import FingerprintJS from '@wec-core/fingerprintjs';
import isFeatureFlagEnabled from '../../helpers/feature-flags';
import { FEATURE_FLAG_ELASTIC_SEARCH } from '../../types/featureFlag.types';
import { logoutOnePass } from '../../helpers/common';

export const PureHeader = props => {
  const { data, baseUrl, shoppingCartUrl } = props;
  const loginButtonUrl = '/personal/my-account/login'; //previously used to come from AEM

  const dispatch = useDispatch();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { hasItemsInCart, productCatalog } = useSelector(state => {
    return {
      hasItemsInCart: state.rrpCart?.addedItems?.length,
      productCatalog: state.productCatalog
    };
  });
  const isActive = productCatalog?.rrpFlow && hasItemsInCart;
  const apiGeeResponseReceivedRef = useRef(false);
  const elasticSearchEnabled = isFeatureFlagEnabled(
    FEATURE_FLAG_ELASTIC_SEARCH
  );

  const { show, onToggleSearch, offsetTop } = useSearchToggle();
  const { navIcons } = data;

  // Extract searchConfig from navIcons data
  const searchNavIcon = navIcons?.find(icon => icon.type === 'search');
  const searchConfig = searchNavIcon?.additionalConfig || {
    searchResultPageUrl: '',
    searchBaseUrl: ''
  };

  useEffect(() => {
    try {
      const isAccessTokenExists = window.sessionStorage.getItem(
        'ApigeeAuthToken'
      );
      if (!isAccessTokenExists) {
        dispatch(
          apigeeAuthActions.getApigeeAuthToken(
            () => (apiGeeResponseReceivedRef.current = true)
          )
        );
      }
      // set a device unique id cookie consistency for parallel api calls
      FingerprintJS.load({ cookies: { 'max-age': '2592000' } });
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInterceptor();

  useEffect(() => {
    // This code to fix menu not showing for mobile, it should return from AEM. Will remove this when aem is ready.
    if (data.primaryNavLinks?.length) {
      data.primaryNavLinks[0].isActive = true;
    }

    const uiamAccessToken = getCookie(CONSTANT.UIAM_ACCESS_TOKEN);
    if (uiamAccessToken && uiamAccessToken.length > 0) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
  useEffect(() => {
    const uiamAccessToken = getCookie(CONSTANT.UIAM_ACCESS_TOKEN);
    if (uiamAccessToken && uiamAccessToken.length > 0) {
      const revokeTokenCall = async () => {
        try {
          const result = await revokeTokenApi(uiamAccessToken);
          if (result?.code === CONSTANT.REVOKE_SUCCESS) {
            deleteCookie(CONSTANT.UIAM_ACCESS_TOKEN);
            window.open(getUIAMLoginUrl(sdAccountSummary, true), '_self');
          }
        } catch (e) {
          setIsLoggedIn(false);
        }
      };

      if (
        header.logoutButtonText &&
        header.avatarMenuItems?.length &&
        !header.avatarMenuItems.some(
          item => item?.title === header.logoutButtonText
        )
      ) {
        header.avatarMenuItems.push({
          title: header.logoutButtonText,
          href: '/logout', // This href does nothing but provide a vanity url if user hovers over the anchor tag
          onClick: e => {
            e.preventDefault();
            revokeTokenCall();
          }
        });
      }
    }
  }, [baseUrl]);
  */

  const handleLogout = async e => {
    e.preventDefault();
    e.stopPropagation();
    await dispatch(eshopUserActions.eshopLogout());
    await logoutOnePass(
      () => {
        // This to make sure that eShop delete the httpOnly cookie
        setTimeout(() => {
          window.open(getUIAMLoginUrl(sdAccountSummary, true), '_self');
        }, 600);
      },
      () => {
        setIsLoggedIn(false);
      }
    );
  };

  // Build menuIcons using data from navIcons with custom logic for each type
  const menuIcons =
    navIcons
      ?.map(navIcon => {
        if (navIcon.type === 'search') {
          return {
            type: 'search',
            additionalConfig: navIcon.additionalConfig,
            onClick: elasticSearchEnabled ? onToggleSearch : undefined
          };
        }
        if (navIcon.type === 'profile') {
          return {
            type: 'profile',
            href: null,
            subMenuItems: isLoggedIn
              ? [
                  {
                    text: 'Edit Profile',
                    href: `${baseUrl}/personal/my-account/my-portal/profile`
                  },
                  {
                    text: 'Logout',
                    href: '#',
                    onClick: handleLogout
                  }
                ]
              : null,
            onClick: isLoggedIn
              ? null
              : () => {
                  if (!isLoggedIn) {
                    window.location.href = `${baseUrl}/${loginButtonUrl}`;
                  }
                }
          };
        }

        if (navIcon.type === 'cart') {
          return {
            type: 'cart',
            isActive,
            onClick: () => {
              navigate(shoppingCartUrl);
            }
          };
        }

        // Return the navIcon as-is for any other types
        return navIcon;
      })
      .filter(Boolean) || [];

  return (
    <HeaderWrapper>
      <DlsHeader
        siteLogo={data?.siteLogo}
        primaryNavLinks={data?.primaryNavLinks}
        menuSections={data?.menuSections}
        navIcons={menuIcons}
      />
      {elasticSearchEnabled && typeof document !== 'undefined' && (
        <SingtelWebSearchBar
          visible={show}
          onToggleVisibility={onToggleSearch}
          type="popup"
          popupStartPosition={offsetTop}
          resultPageUrl={searchConfig.searchResultPageUrl}
          apiBaseUrl={searchConfig.searchBaseUrl}
        />
      )}
    </HeaderWrapper>
  );
};

/* istanbul ignore next */
const Header = props => {
  const { isClient, isWebView, flow } = useIsWebViewFlow();

  const hideHeader = !isClient || (isClient && isWebView);

  return (
    <StaticQuery
      key={flow}
      query={graphql`
        {
          singtelHeader {
            businessHeader
            menuSections {
              text
              isActive
              href
              alt
              isHighlights
              subMenuItems {
                alt
                href
                isActive
                isHighlights
                subMenuItems {
                  alt
                  text
                  isHighlights
                  isActive
                  href
                }
                text
              }
            }
            siteLogo {
              href
              alt
              sourceUrl
            }
            primaryNavLinks {
              isActive
              href
              isSwitcher
              switcherLabel
              text
            }
            navIcons {
              type
              subMenuItems {
                href
                text
              }
              href
              additionalConfig {
                rinput
                searchBaseUrl
                searchResultPageUrl
                customSearch
              }
            }
          }
        }
      `}
      render={data => {
        return (
          <PureHeader
            data={data.singtelHeader}
            {...props}
            hideHeader={hideHeader}
          />
        );
      }}
    />
  );
};

export default Header;
